import { Theme, createTheme as createMuiTheme } from "@mui/material/styles";

export type ThemeColor =
  | "primary"
  | "red"
  | "fuchsia"
  | "yellow"
  | "blue"
  | "green"
  | "purple"
  | "google";

declare module "@mui/material/styles/createPalette" {
  // Optional so this augmentation merges cleanly with @tiny/theme's palette
  // augmentation (which declares the same members as optional) when both are
  // loaded in one program (TinyAlbert). The output `Palette` interface below
  // keeps them required, so reads (theme.palette.google) are unaffected, and
  // email still provides them.
  interface PaletteOptions {
    red?: PaletteColorOptions;
    fuchsia?: PaletteColorOptions;
    yellow?: PaletteColorOptions;
    blue?: PaletteColorOptions;
    green?: PaletteColorOptions;
    purple?: PaletteColorOptions;
    google?: PaletteColorOptions;
    darkBlue?: PaletteColor;
  }
  interface Palette {
    red: PaletteColor;
    fuchsia: PaletteColor;
    yellow: PaletteColor;
    blue: PaletteColor;
    green: PaletteColor;
    purple: PaletteColor;
    google: PaletteColor;
    darkBlue: PaletteColor;
  }

  interface PaletteColor {
    lighter: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
  }
}

// Make @mui/styles' makeStyles/withStyles use the full MUI theme type.
// The type-only import forces TS to include the module in the program so the
// augmentation below resolves even before any file imports @mui/styles.
import type {} from "@mui/styles/defaultTheme";
declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

type CreateThemeOptions = {
  dark: boolean;
};

const lightGrey = {
  50: "#FBFCFD",
  100: "#F4F6FB",
  200: "#E7E9EE",
  300: "#DFE0E5",
  400: "#CFD0D4",
  500: "#C9CACE",
  600: "#9FA1A8",
  700: "#6F737D",
  800: "#3F4451",
  900: "#0F1526",
};

const darkGrey = {
  50: "#FBFCFD",
  100: "#F4F6FB",
  200: "#E7E9EE",
  300: "#DFE0E5",
  400: "#CFD0D4",
  500: "#C9CACE",
  600: "#9FA1A8",
  700: "#6F737D",
  800: "#3F4451",
  900: "#0F1526",
};

function createTheme(options: CreateThemeOptions): Theme {
  const theme = createMuiTheme({
    // KEY ORDER MATTERS: charts pick slice colours by walking
    // Object.keys(theme.palette) (see useColorAccessor), and v5 orders
    // user-supplied keys first in source order, where v4 put its own defaults
    // first. Keep primary -> secondary -> error -> warning -> info -> success, or
    // the ISP breakdown pie leads with `error` and turns red. The explicit
    // secondary/info/success/warning values matter too — v5 changed all four
    // defaults and this app never set them.
    palette: {
      primary: {
        lighter: "#98DDCA",
        light: "#299E90",
        main: "#008060",
        dark: "#005741",
        contrastText: "#ffffff",
      },
      secondary: {
        lighter: "#F8BBD0",
        light: "#FF4081",
        main: "#F50057",
        dark: "#C51162",
      },
      error: {
        lighter: "#FFE7E6",
        main: "#E57170",
      },
      warning: {
        lighter: "#FFE0B2",
        light: "#FFB74D",
        main: "#FF9800",
        dark: "#F57C00",
      },
      info: {
        lighter: "#BBDEFB",
        light: "#64B5F6",
        main: "#2196F3",
        dark: "#1976D2",
      },
      success: {
        lighter: "#C8E6C9",
        light: "#81C784",
        main: "#4CAF50",
        dark: "#388E3C",
      },
      grey: options.dark ? darkGrey : lightGrey,
      background: {
        paper: "#fff",
        default: options.dark ? "#0F1526" : "#fff",
      },
      mode: options.dark ? "dark" : "light",
      red: {
        lighter: "#FFE7E6",
        light: "#FFB0AF",
        main: "#FB8B8A",
        dark: "#E57170",
        contrastText: "#ffffff",
      },
      fuchsia: {
        lighter: "#FFE2FE",
        light: "#FFB5DE",
        main: "#EF9ACC",
        dark: "#DC89B9",
        contrastText: "#ffffff",
      },
      yellow: {
        lighter: "#FFF3D6",
        light: "#FFE7AD",
        main: "#FFD97D",
        dark: "#EAC263",
        contrastText: "#ffffff",
      },
      blue: {
        lighter: "#BDF4FF",
        light: "#6EDEF4",
        main: "#48CAE4",
        dark: "#41B8D0",
        contrastText: "#ffffff",
      },
      darkBlue: {
        lighter: "#BDF4FF",
        light: "#6EDEF4",
        main: "#48CAE4",
        dark: "#30344e",
        contrastText: "#ffffff",
      },
      green: {
        lighter: "#D0FFFA",
        light: "#AFFFF6",
        main: "#88F7EA",
        dark: "#69E3D5",
        contrastText: "#ffffff",
      },
      purple: {
        lighter: "#F0EBFF",
        light: "#DBCFFE",
        main: "#C4AFFF",
        dark: "#734FF7",
        contrastText: "#ffffff",
      },
      google: {
        lighter: "#F4F6FB",
        light: "#F4F6FB",
        main: "#F4F6FB",
        dark: "#F4F6FB",
        contrastText: "#0F1526",
      },
    },
    spacing: 4,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 1024,
        lg: 1366,
        xl: 1920,
      },
    },
    typography: {
      fontFamily: "Archivo",
    },
    components: {
      // MUI v5 changed the Link default `underline` from "hover" (v4) to
      // "always". Restore the v4 behaviour globally so links are only
      // underlined on hover, matching the reference app.
      MuiLink: {
        defaultProps: {
          underline: "hover",
        },
      },
      // MUI v5's CssBaseline applies `typography.body1` (16px / line-height 1.5)
      // to <body>, whereas v4 applied `body2` (14px / line-height 1.43). That
      // shifts every INHERITED text node app-wide. Restore the v4 baseline so
      // inherited text matches the reference app.
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            fontSize: "0.875rem",
            lineHeight: 1.43,
          },
        },
      },
    },
  });

  return theme;
}

export default createTheme;
